// Streaming for the Playground. POSTs to the tenant-admin stream endpoint to
// start the runtime loop in the background, then subscribes to the run's Ably
// channel to render tokens + status events as they are published.
//
// This is the only streaming client. There used to be a second one reading an
// SSE response for a tools-free preview; that preview is now `preview: true` on
// this same call — the same loop with tools and validators switched off. One
// transport, and nothing that needs an HTTP response held open (which a Lambda
// invocation cannot do).
//
// Ably note: the `ably` npm package's builds call `super(...)` inside a nested
// function in one class — invalid per spec, so webpack/SWC refuse to bundle it
// (and `transpilePackages` makes it worse by downleveling). We instead load
// Ably's prebuilt browser UMD (self-hosted at /vendor/ably.min.js, copied from
// node_modules/ably/build/ably.min.js) via a <script> tag at runtime, which the
// browser parses directly — no webpack involved. Types stay decoupled (local
// interfaces) so nothing imports the npm module.

import type { GenerationParams } from "@/types/api";

import { apiFetch } from "./client";

export interface GovernedHandoff {
  run_id: string;
  ably_channel: string;
  // An Ably TokenRequest the browser hands straight to Ably, OR a
  // `{ stub: true, ... }` marker when the server has no ABLY_API_KEY.
  ably_token: Record<string, unknown>;
}

/** Kick off a run; returns the info needed to subscribe to Ably.
 *
 * `preview` runs the same loop without tools or validators — for iterating on
 * soul/instructions without tool noise. It is still checkpointed and audited.
 */
export function startGovernedStream(
  agentSlug: string,
  label: string,
  turns: { role: string; content: string }[],
  preview = false,
  /** One-run sampling override over the version's saved params. */
  generation: GenerationParams | null = null,
): Promise<GovernedHandoff> {
  return apiFetch<GovernedHandoff>(
    `/admin/my/agents/${agentSlug}/versions/${label}/playground/stream`,
    { method: "POST", body: generation ? { turns, preview, generation } : { turns, preview } },
  );
}

/** Kick off a skill as a deferred run; same handoff shape as a chat run.
 *
 * The frozen `/api/v1` skill endpoint stays synchronous — it must return its
 * envelope in the body — so this is a separate, additive route for callers that
 * can accept a 202. A long skill belongs here: it is not racing a request
 * timeout.
 */
export function startSkillStream(
  agentSlug: string,
  label: string,
  skillSlug: string,
  input: Record<string, unknown>,
): Promise<GovernedHandoff> {
  return apiFetch<GovernedHandoff>(
    `/admin/my/agents/${agentSlug}/versions/${label}/skills/${skillSlug}/stream`,
    { method: "POST", body: { input } },
  );
}

// ---- Ably UMD loader (runtime <script>, not a webpack import) --------------

// Minimal structural types for the bits of the Ably realtime client we touch.
interface AblyMessage {
  name?: string;
  data?: unknown;
}
interface AblyChannel {
  subscribe(listener: (msg: AblyMessage) => void): unknown;
  unsubscribe(): void;
}
interface AblyRealtime {
  channels: { get(name: string): AblyChannel };
  connection: { on(event: string, listener: (...a: unknown[]) => void): void };
  close(): void;
}
interface AblyStatic {
  Realtime: new (options: Record<string, unknown>) => AblyRealtime;
}

declare global {
  interface Window {
    Ably?: AblyStatic;
  }
}

let ablyPromise: Promise<AblyStatic> | null = null;

/** Load the self-hosted Ably UMD once; resolve `window.Ably`. */
function loadAbly(): Promise<AblyStatic> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Ably is only available in the browser"));
  }
  if (window.Ably) return Promise.resolve(window.Ably);
  if (ablyPromise) return ablyPromise;
  ablyPromise = new Promise<AblyStatic>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "/vendor/ably.min.js";
    s.async = true;
    s.onload = () =>
      window.Ably ? resolve(window.Ably) : reject(new Error("Ably global did not load"));
    s.onerror = () => reject(new Error("Failed to load the Ably client"));
    document.head.appendChild(s);
  });
  return ablyPromise;
}

// ---- Subscription ---------------------------------------------------------

/** Explicit terminal run notification kinds. */
const TERMINAL = new Set(["run_completed", "run_failed", "run_interrupted"]);
/** Phases that are themselves terminal — the loop publishes a `phase_started`
 * for these but not always a distinct `run_*` notification, so we treat the
 * phase transition as the end and normalize it to a `run_<phase>` kind. */
const TERMINAL_PHASES = new Set(["completed", "failed", "interrupted"]);

export interface RunSubscription {
  onToken: (text: string) => void;
  // Non-token lifecycle events (phase_started, tool_call_*, validation_rejected…).
  onEvent: (kind: string, payload: Record<string, unknown>) => void;
  onDone: (kind: string, payload: Record<string, unknown>) => void;
  onError: (message: string) => void;
}

/** Returns `true` when the handoff is a dev stub (no real Ably to subscribe to). */
export function isStubHandoff(handoff: GovernedHandoff): boolean {
  return (handoff.ably_token as { stub?: boolean }).stub === true;
}

/**
 * Subscribe to a governed run's Ably channel. Returns a cleanup function that
 * closes the connection; it is also called automatically on the terminal event.
 *
 * The browser never holds the Ably API key — it authenticates with the
 * capability-scoped (subscribe-only) TokenRequest the server minted for this
 * exact channel.
 */
export function subscribeRun(handoff: GovernedHandoff, handlers: RunSubscription): () => void {
  if (isStubHandoff(handoff)) {
    handlers.onError(
      "Live streaming needs ABLY_API_KEY set on the server — the run started but there is no channel to subscribe to.",
    );
    return () => {};
  }

  let client: AblyRealtime | null = null;
  let channel: AblyChannel | null = null;
  let closed = false;
  const cleanup = () => {
    if (closed) return;
    closed = true;
    try {
      channel?.unsubscribe();
      client?.close();
    } catch {
      /* best effort */
    }
  };

  loadAbly()
    .then((Ably) => {
      if (closed) return;
      client = new Ably.Realtime({
        // Hand the server-minted TokenRequest straight to Ably. No API key here.
        authCallback: (_params: unknown, callback: (err: unknown, token: unknown) => void) =>
          callback(null, handoff.ably_token),
      });
      channel = client.channels.get(handoff.ably_channel);

      channel.subscribe((msg: AblyMessage) => {
        const kind = msg.name ?? "";
        const data = (msg.data ?? {}) as { payload?: Record<string, unknown> };
        const payload = data.payload ?? {};

        if (kind === "token") {
          const text = typeof payload.text === "string" ? payload.text : "";
          if (text) handlers.onToken(text);
        } else if (kind) {
          handlers.onEvent(kind, payload);
        }

        // Resolve terminal state from an explicit run_* event OR a transition
        // into a terminal phase, normalizing both to a `run_<phase>` kind.
        let terminalKind: string | null = null;
        if (TERMINAL.has(kind)) {
          terminalKind = kind;
        } else if (kind === "phase_started" && TERMINAL_PHASES.has(String(payload.phase))) {
          terminalKind = `run_${String(payload.phase)}`;
        }
        if (terminalKind) {
          handlers.onDone(terminalKind, payload);
          cleanup();
        }
      });

      client.connection.on("failed", () => {
        handlers.onError("Ably connection failed.");
        cleanup();
      });
    })
    .catch((e: unknown) => {
      handlers.onError(e instanceof Error ? e.message : "Failed to load Ably");
    });

  return cleanup;
}
